import * as XLSX from 'xlsx';
import { mergeErpProductImportPriceHeaders } from './erp-product-import-price.util';

/**
 * Même logique de colonnes / en-têtes que `products.component.ts` (import XLSX produits).
 */
export function normalizeImportHeaderLabel(h: any): string {
    if (h == null || h === '') {
        return '';
    }
    return String(h)
        .trim()
        .replace(/\u00a0/g, ' ')
        .replace(/_/g, ' ')
        .replace(/-/g, ' ')
        .replace(/\s+/g, ' ')
        .toUpperCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

export interface ProductXlsxParseResult {
    headerMap: Record<string, number>;
    dataRows: any[][];
    missingLabels: string[];
    /** true si en-têtes type logiciel-caisse (CODE_ART / DES_ART / GENC…). */
    isCaisseFormat: boolean;
    /** Index 0-based de la ligne d’en-têtes dans la feuille (première ligne contenant DES_ART / NOM). */
    headerRowIndex: number;
    /** Ligne brute d’en-têtes (pour re-résolution des colonnes prix). */
    headerRow: unknown[];
}

/** Repère la ligne d’en-têtes (export caisse avec éventuelle ligne de titre au-dessus). */
export function findProductImportHeaderRowIndex(jsonData: unknown[][]): number {
    const max = Math.min(15, jsonData.length);
    for (let r = 0; r < max; r++) {
        const row = jsonData[r] ?? [];
        const headers = (Array.isArray(row) ? row : []).map((h) => normalizeImportHeaderLabel(h));
        if (
            headers.includes('DES ART') ||
            headers.includes('NOM') ||
            headers.includes('DESIGNATION') ||
            headers.includes('LIBELLE')
        ) {
            return r;
        }
    }
    return 0;
}

/**
 * Modèle d’import aligné logiciel-caisse (biblio-test / front-achat / front-vente).
 * Colonnes : CODE_ART, ART_PARENT, DES_ART, GENC, vente ttc, FAMILLE…
 */
export const PRODUCT_XLSX_IMPORT_HEADERS: string[] = [
    'CODE_ART',
    'NIVEAU',
    'est parent',
    'ART_PARENT',
    'DES_ART',
    'unite',
    'UNITE VENTE',
    'achat ttc',
    'tva',
    'achat ht',
    'vente ttc',
    'TVA VENTE',
    'vente ht',
    'gerable',
    'vendable',
    'SAISON',
    'fournisseur',
    'GENC',
    'FAMILLE',
    'SOUS FAMILLE',
    'taille',
    'couleur',
    'RAYON',
    ...Array.from({ length: 12 }, (_, i) => `PARAM${i + 1}`)
];

/** Ligne d’exemple (article parent) pour le modèle téléchargeable. */
export const PRODUCT_XLSX_IMPORT_SAMPLE_ROW: string[] = [
    '52260001',
    '1',
    '1',
    '52260001',
    'FOULARD FEMME',
    'unité',
    'unité',
    '14,4228',
    '19',
    '12',
    '29,9',
    '19',
    '25,12605042',
    '1',
    '1',
    '26E',
    '401001108',
    '',
    'ACCESSOIRE',
    'FOULARD',
    '',
    '',
    'TOUS',
    'ACCESSOIRE',
    'SPORT',
    'NOEUD',
    'SANS ANIMATION',
    'ACCESSOIRE',
    'FLORAL',
    'VIDE',
    'COTON',
    'ESSENTIEL',
    'AUCUN',
    'EXTRA',
    'CHAINE ET TRAME'
];

export function createEmptyProductImportWorkbook(): XLSX.WorkBook {
    const worksheet = XLSX.utils.aoa_to_sheet([
        PRODUCT_XLSX_IMPORT_HEADERS,
        PRODUCT_XLSX_IMPORT_SAMPLE_ROW
    ]);
    const colWidths = Array.from({ length: PRODUCT_XLSX_IMPORT_HEADERS.length }, () => ({ wch: 14 }));
    colWidths[0] = { wch: 14 };
    colWidths[3] = { wch: 14 };
    colWidths[4] = { wch: 28 };
    colWidths[17] = { wch: 16 };
    worksheet['!cols'] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Produits');
    return workbook;
}

/** Flag « est parent » (1 / oui / true…). */
export function isTruthyImportFlag(v: string | null | undefined): boolean {
    const s = String(v ?? '')
        .trim()
        .toLowerCase();
    return s === '1' || s === 'true' || s === 'oui' || s === 'yes' || s === 'vrai';
}

/**
 * Article parent caisse : `est parent` OU (sans GENC et CODE_ART = ART_PARENT).
 * Non persisté à l’import — sert au regroupement des variantes.
 */
export function isCaisseParentArticleRow(opts: {
    estParent?: string | null;
    codeArt?: string | null;
    artParent?: string | null;
    codeBarre?: string | null;
}): boolean {
    const codeArt = String(opts.codeArt ?? '').trim();
    const artParent = String(opts.artParent ?? '').trim();
    const codeBarre = String(opts.codeBarre ?? '').trim();
    if (isTruthyImportFlag(opts.estParent)) {
        return true;
    }
    return !codeBarre && !!codeArt && !!artParent && codeArt === artParent;
}

export function parseProductXlsxImportSheet(arrayBuffer: ArrayBuffer): ProductXlsxParseResult | { error: string } {
    try {
        const data = new Uint8Array(arrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheet];
        const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

        const headerRowIndex = findProductImportHeaderRowIndex(jsonData);
        const rawHeaders = jsonData[headerRowIndex] ?? [];
        const headers = rawHeaders.map((h: any) => normalizeImportHeaderLabel(h));

        const resolveHeaderIndex = (aliases: string[]): number => {
            const normalizedAliases = aliases
                .map((a) => normalizeImportHeaderLabel(a))
                .filter((a) => a.length > 0);
            for (let i = 0; i < headers.length; i++) {
                const cellH = headers[i];
                if (cellH && normalizedAliases.includes(cellH)) {
                    return i;
                }
            }
            return -1;
        };

        const idxNom = resolveHeaderIndex([
            'DES ART',
            'DES_ART',
            'NOM',
            'NAME',
            'NOM DU PRODUIT',
            'LIBELLE',
            'DESIGNATION'
        ]);
        const idxType = resolveHeaderIndex(['TYPE']);
        const idxPrix = resolveHeaderIndex([
            'PRIX VENTE',
            'PRIX DE VENTE',
            'PRIX_VENTE',
            'PRIX UNITAIRE',
            'PRIX',
            'PRIX_UNITAIRE',
            'PRIXUNITAIRE',
            'PU'
        ]);
        const idxCodeArt = resolveHeaderIndex(['CODE ART', 'CODE_ART', 'CODE ARTICLE']);
        const idxArtParent = resolveHeaderIndex(['ART PARENT', 'ART_PARENT']);
        const idxEstParent = resolveHeaderIndex(['EST PARENT', 'EST_PARENT']);
        const idxRefInt = resolveHeaderIndex([
            'RÉFÉRENCE INTERNE',
            'REFERENCE INTERNE',
            'REF INTERNE',
            'REF_INTERNE',
            'REF. INTERNE',
            'REF PRODUIT',
            'CODE ART',
            'CODE_ART',
            'CODE ARTICLE'
        ]);
        const idxDesc = resolveHeaderIndex(['DESCRIPTION', 'DESCR', 'DETAILS', 'DETAIL']);
        const idxResultTtc = resolveHeaderIndex([
            'RESULT TTC',
            'RESULTAT TTC',
            'RESULT_TTC',
            'RESULT-TTC',
            'PRIX TTC',
            'RESULT T.T.C'
        ]);
        const idxResultHt = resolveHeaderIndex([
            'RESULT HT',
            'RESULTAT HT',
            'RESULT_HT',
            'RESULT-HT',
            'PRIX HT',
            'RESULT H.T'
        ]);

        const isCaisseFormat =
            idxCodeArt >= 0 ||
            idxArtParent >= 0 ||
            resolveHeaderIndex(['GENC']) >= 0 ||
            resolveHeaderIndex(['DES ART', 'DES_ART']) >= 0 ||
            resolveHeaderIndex(['EST PARENT', 'EST_PARENT']) >= 0 ||
            resolveHeaderIndex(['VENTE TTC']) >= 0;

        // Aligné logiciel-caisse : seule DES_ART / NOM est obligatoire à l’étape parse.
        // Type / prix / réf. sont validés ligne par ligne (aperçu + import).
        const missingLabels: string[] = [];
        if (idxNom < 0) {
            missingLabels.push('DES_ART (ou NOM)');
        }
        if (missingLabels.length > 0) {
            return {
                headerMap: {},
                dataRows: [],
                missingLabels,
                isCaisseFormat,
                headerRowIndex,
                headerRow: rawHeaders
            };
        }

        const headerMap: Record<string, number> = {
            nom: idxNom,
            type: idxType,
            prix: idxPrix,
            ref_interne: idxRefInt >= 0 ? idxRefInt : idxCodeArt,
            code_art: idxCodeArt,
            art_parent: idxArtParent,
            est_parent: idxEstParent,
            description: idxDesc,
            result_ttc: idxResultTtc,
            result_ht: idxResultHt,
            ref_fabricant: resolveHeaderIndex([
                'RÉFÉRENCE FABRICANT',
                'REFERENCE FABRICANT',
                'RÉFÉRENCE FABRIQUANT',
                'REFERENCE FABRIQUANT',
                'REF FABRICANT',
                'REF FABRIQUANT',
                'REF_FABRICANT',
                'REF_FABRIQUANT',
                'ART PARENT',
                'ART_PARENT'
            ]),
            code_barre: resolveHeaderIndex([
                'GENC',
                'CODE BARRE',
                'CODE_BARRE',
                'CODE-BARRE',
                'CODEBARRE',
                'EAN',
                'GTIN'
            ]),
            couleur: resolveHeaderIndex(['COULEUR']),
            taille: resolveHeaderIndex(['TAILLE']),
            rayon: resolveHeaderIndex(['RAYON']),
            famille: resolveHeaderIndex(['FAMILLE']),
            sous_famille: resolveHeaderIndex([
                'SOUS FAMILLE',
                'SOUS_FAMILLE',
                'SOUS-FAMILLE',
                'SOUS FAMILLE'
            ]),
            tva: resolveHeaderIndex([
                'TVA VENTE',
                'TVA',
                'TAUX TVA',
                'TAUX DE TVA',
                'TAUX',
                'POURCENTAGE TVA',
                'VALEUR TVA',
                'TVA %',
                '% TVA',
                'TAUX T V A'
            ]),
            taxe: resolveHeaderIndex(['TAXE', 'TAXES']),
            marque: resolveHeaderIndex(['MARQUE']),
            categorie: resolveHeaderIndex([
                'CATEGORIE',
                'CATÉGORIE',
                'CATEGORY',
                'CATEGORIE PRODUIT',
                'FAMILLE'
            ]),
            unite: resolveHeaderIndex([
                'UNITE VENTE',
                'UNITE',
                'UNITÉ',
                'UNIT',
                'UNITÉ DE VENTE'
            ]),
            quantite: resolveHeaderIndex(['QUANTITE', 'QUANTITÉ', 'QTE', 'QTY', 'QUANTITY'])
        };
        for (let p = 1; p <= 12; p++) {
            headerMap[`param${p}`] = resolveHeaderIndex([`PARAM${p}`, `PARAM ${p}`]);
        }

        const dataRows = jsonData
            .slice(headerRowIndex + 1)
            .filter((row) => row.some((cell: any) => cell != null && String(cell).trim() !== ''));

        return {
            headerMap: mergeErpProductImportPriceHeaders(headerMap, jsonData[headerRowIndex] ?? []),
            dataRows,
            missingLabels: [],
            isCaisseFormat,
            headerRowIndex,
            headerRow: jsonData[headerRowIndex] ?? []
        };
    } catch (e: any) {
        return { error: e?.message || String(e) };
    }
}

export function readImportCellTrimmed(row: any[], headerMap: Record<string, number>, key: string): string | null {
    const idx = headerMap[key];
    if (idx == null || idx < 0) {
        return null;
    }
    const v = row[idx];
    if (v === undefined || v === null || v === '') {
        return null;
    }
    return String(v).trim();
}

export function parseTvaImportValue(raw: string): number | null {
    const cleaned = String(raw)
        .trim()
        .replace(/%/g, '')
        .replace(/\s/g, '')
        .replace(',', '.');
    if (cleaned === '') {
        return null;
    }
    const n = parseFloat(cleaned);
    return isNaN(n) ? null : n;
}

function preparePriceListsForImport(
    prix_ht: number,
    prix_ttc: number,
    clientId: number,
    stationId: number
): any {
    return {
        prix_id: 1,
        bascul_calcul: 'prix_public',
        regles: 'fixe',
        montant: Number(prix_ht),
        result_ttc: Number(prix_ttc),
        result_ht: Number(prix_ht),
        client_id: clientId,
        station_id: stationId
    };
}

function prepareWarehousesForImport(
    stock_minimum: number,
    stock_maximum: number,
    clientId: number,
    stationId: number
): any[] {
    return [
        {
            entrepot_id: 1,
            minimum: stock_minimum,
            maximum: stock_maximum,
            is_active_alert: stock_minimum > 0,
            client_id: clientId,
            station_id: stationId
        }
    ];
}

/** Alignement payload POST produit (identique à `products.component.applyImportProductApiShape`). */
export function applyDocumentImportProductApiShape(
    product: Record<string, any>,
    clientId: number,
    stationId: number
): void {
    const tid = product.tva_id;
    if (tid != null && tid !== '' && !Number.isNaN(Number(tid))) {
        product.tva_id = Math.floor(Number(tid));
    }

    if (!Array.isArray(product.taxes)) {
        const tx = product.taxe_id;
        if (tx != null && Number(tx) > 0) {
            product.taxes = [
                {
                    taxe_id: Number(tx),
                    client_id: product.client_id,
                    station_id: product.station_id
                }
            ];
        } else {
            product.taxes = [];
        }
    }

    if (!Array.isArray(product.liste_prix) || product.liste_prix.length === 0) {
        const pu = Number(
            product.vente_ht ?? product.vente_ttc ?? product.prix_unitaires ?? 0
        );
        const rht =
            product.vente_ht != null && product.vente_ht !== ''
                ? Number(product.vente_ht)
                : product.result_ht != null
                  ? Number(product.result_ht)
                  : null;
        const rtc =
            product.vente_ttc != null && product.vente_ttc !== ''
                ? Number(product.vente_ttc)
                : product.result_ttc != null
                  ? Number(product.result_ttc)
                  : null;
        if (pu > 0 || (rht != null && !Number.isNaN(rht)) || (rtc != null && !Number.isNaN(rtc))) {
            const ht = rht != null && !Number.isNaN(rht) ? rht : pu;
            const ttc = rtc != null && !Number.isNaN(rtc) ? rtc : pu > 0 ? pu : ht;
            product.liste_prix = [preparePriceListsForImport(ht, ttc, clientId, stationId)];
        } else if (product.tva_id != null && Number(product.tva_id) > 0) {
            const ht = Number(product.result_ht ?? 0);
            const ttc = Number(product.result_ttc ?? ht);
            product.liste_prix = [preparePriceListsForImport(ht, ttc, clientId, stationId)];
        } else {
            product.liste_prix = [];
        }
    }

    if (!Array.isArray(product.information_logistique)) {
        const typeNorm = String(product.type || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
        const isMateriel = typeNorm === 'materiel';
        product.information_logistique = isMateriel
            ? prepareWarehousesForImport(0, 0, clientId, stationId)
            : [];
    }

    if (product.autoriser_vente_stock_vide === undefined) {
        product.autoriser_vente_stock_vide = false;
    }
    if (product.produit_avec_numero_serie === undefined) {
        product.produit_avec_numero_serie = false;
    }
}
